import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";
import type { GameModeId } from "../levels/types";

export interface LobbyServerRecord {
  serverId: string;
  name: string;
  mapId: string;
  mode: GameModeId;
  maxPlayers: number;
  currentPlayers: number;
  hostName: string;
  createdAt: string;
}

export interface PublishedLobbyServer {
  channel: RealtimeChannel;
  update: (patch: Partial<LobbyServerRecord>) => Promise<void>;
  dispose: () => void;
}

function mapPresenceToServers(channel: RealtimeChannel) {
  const state = channel.presenceState();
  const servers = Object.values(state)
    .flatMap((entries) => entries as Array<{ server?: LobbyServerRecord }>)
    .map((entry) => entry.server)
    .filter(Boolean) as LobbyServerRecord[];

  return servers.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export function subscribeToLobbyServers(onUpdate: (servers: LobbyServerRecord[]) => void) {
  const channel = supabase.channel("cortex:servers", {
    config: {
      presence: {
        key: crypto.randomUUID(),
      },
    },
  });

  channel
    .on("presence", { event: "sync" }, () => {
      onUpdate(mapPresenceToServers(channel));
    })
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

export function publishLobbyServer(server: LobbyServerRecord): PublishedLobbyServer {
  const channel = supabase.channel("cortex:servers", {
    config: {
      presence: {
        key: server.serverId,
      },
    },
  });

  let current = server;

  channel.subscribe(async (status) => {
    if (status === "SUBSCRIBED") {
      await channel.track({ server: current });
    }
  });

  return {
    channel,
    update: async (patch) => {
      current = { ...current, ...patch };
      await channel.track({ server: current });
    },
    dispose: () => {
      supabase.removeChannel(channel);
    },
  };
}